/**
 * The sales report: a range of whole days, and what was sold in it.
 *
 * DAYS ARE ISTANBUL DAYS. The operator reads "today" and "last month" on an
 * Istanbul clock, and a day cut at UTC midnight would put three evening hours
 * of sales on the next day. Turkey has kept UTC+3 all year since 2016, so the
 * offset is a constant, not a lookup; the worker and the panel both cut days
 * with it, which is why it lives here.
 */
export const ISTANBUL_OFFSET_HOURS = 3;
/** A year and a day: a whole leap year, the longest range worth asking for. */
export const MAX_REPORT_DAYS = 366;
const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
/** A real calendar day in `YYYY-MM-DD`: `2026-02-30` is not one. */
export function isDay(value) {
    if (typeof value !== "string" || !DAY_RE.test(value))
        return false;
    const date = new Date(`${value}T00:00:00Z`);
    return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
}
/** The UTC instant an Istanbul day starts, as SQLite writes times. */
export function istanbulMidnightUtc(day) {
    const utc = Date.parse(`${day}T00:00:00Z`) - ISTANBUL_OFFSET_HOURS * 3_600_000;
    return new Date(utc).toISOString().slice(0, 19).replace("T", " ");
}
/** The Istanbul day an instant falls on. */
export function istanbulDay(at) {
    return new Date(at.getTime() + ISTANBUL_OFFSET_HOURS * 3_600_000).toISOString().slice(0, 10);
}
/** A day `n` days later (or earlier, for a negative `n`). */
export function addDays(day, n) {
    const date = new Date(`${day}T00:00:00Z`);
    date.setUTCDate(date.getUTCDate() + n);
    return date.toISOString().slice(0, 10);
}
/** How many days `from` to `to` spans, both counted. */
export function daysIn(from, to) {
    const ms = Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`);
    return Math.round(ms / 86_400_000) + 1;
}
/** The last `days` Istanbul days, today counted: "the last 30 days". */
export function lastDays(days, now) {
    const today = istanbulDay(now);
    return { from: addDays(today, 1 - days), to: today };
}
/** A range the report takes: two real days, in order, a year and a day at most. */
export function readRange(from, to) {
    if (!isDay(from) || !isDay(to)) {
        return { ok: false, message: "Expected from and to as YYYY-MM-DD" };
    }
    if (from > to)
        return { ok: false, message: "The range ends before it starts" };
    if (daysIn(from, to) > MAX_REPORT_DAYS) {
        return { ok: false, message: `A report covers at most ${MAX_REPORT_DAYS} days` };
    }
    return { ok: true, range: { from, to } };
}
//# sourceMappingURL=report.js.map